import AsyncStorage from '@react-native-async-storage/async-storage';
import CryptoJS from 'crypto-js';
import * as SecureStore from 'expo-secure-store';
import 'react-native-get-random-values';
import { v4 as uuidv4 } from 'uuid';

/**
 * Encrypted write-through local drafts for dynamic forms (CD-222).
 *
 * The state machine and the in-progress section's field values live in memory
 * only; an app kill mid-section loses them. Drafts shrink that loss window to
 * the debounce interval. Payloads contain participant answers (PHI), so they
 * are AES-encrypted at rest in AsyncStorage with a per-device cipher key kept
 * in the Keychain/Keystore via SecureStore.
 */

export interface FormDraftPayload<FormValuesType = any> {
  formValues: Partial<FormValuesType>;
  statePath?: string;
  history?: string[];
  formTemplateVersion?: number;
  /** entityDataId or participantEventLogId; absent until the first save */
  instanceId?: number | string;
  savedAt: number;
}

export type FormDraftInput<FormValuesType = any> = Omit<
  FormDraftPayload<FormValuesType>,
  'savedAt'
>;

const DRAFT_KEY_PREFIX = 'formDraft:';
const CIPHER_KEY_STORE_KEY = 'formDraftCipherKey';

/** Drafts older than this are stale — discard rather than restore. */
export const FORM_DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Screens gate their first render on the draft check, so a hung storage or
 * Keychain call must degrade to "no draft" rather than blocking the form.
 */
export const FORM_DRAFT_LOAD_TIMEOUT_MS = 3000;

const withTimeout = <T>(
  work: Promise<T>,
  fallback: T,
  ms: number,
  onTimeout?: () => void
): Promise<T> =>
  new Promise<T>(resolve => {
    const timer = setTimeout(() => {
      onTimeout?.();
      resolve(fallback);
    }, ms);
    work
      .then(result => {
        clearTimeout(timer);
        resolve(result);
      })
      .catch(() => {
        clearTimeout(timer);
        resolve(fallback);
      });
  });

/**
 * One key per user and template — the instance identity lives in the payload
 * and is validated on restore. Keeping the key stable across the first save
 * (which assigns the entity id mid-flow) avoids a key migration entirely.
 */
export const formDraftKey = ({
  userId,
  formTemplateId,
  instanceId
}: {
  userId: number | string;
  formTemplateId: number | string;
  instanceId?: number | string;
}): string =>
  instanceId == null
    ? `${DRAFT_KEY_PREFIX}${userId}:${formTemplateId}`
    : `${DRAFT_KEY_PREFIX}${userId}:${formTemplateId}:${instanceId}`;

/** Route params arrive as strings while created ids are numbers. */
const sameInstance = (a?: number | string, b?: number | string): boolean =>
  (a == null && b == null) || String(a) === String(b);

/**
 * The cipher key is generated once per device and lives in the
 * Keychain/Keystore. The lookup is shared so concurrent first accesses cannot
 * race two different keys into existence.
 */
let cipherKeyLookup: Promise<string> | undefined;

const getCipherKey = (): Promise<string> => {
  if (!cipherKeyLookup) {
    cipherKeyLookup = (async () => {
      const stored = await SecureStore.getItemAsync(CIPHER_KEY_STORE_KEY);
      if (stored) return stored;
      const generated = `${uuidv4()}${uuidv4()}`;
      await SecureStore.setItemAsync(CIPHER_KEY_STORE_KEY, generated);
      return generated;
    })().catch(error => {
      // do not cache a rejection — retry on the next access
      cipherKeyLookup = undefined;
      throw error;
    });
  }
  return cipherKeyLookup;
};

export const saveFormDraft = async (
  draftKey: string,
  draft: FormDraftInput,
  savedAt: number = Date.now()
): Promise<void> => {
  try {
    const payload: FormDraftPayload = { ...draft, savedAt };
    const cipherKey = await getCipherKey();
    const ciphertext = CryptoJS.AES.encrypt(
      JSON.stringify(payload),
      cipherKey
    ).toString();
    await AsyncStorage.setItem(draftKey, ciphertext);
  } catch {
    // a failed draft write must never break the form — the draft is a
    // best-effort recovery net, not required state
  }
};

export interface FormDraftLoadOptions {
  formTemplateVersion?: number;
  instanceId?: number | string;
  now?: number;
  /**
   * Whether the form may resume at a stored statePath. A path that fails is
   * dropped from the result and from storage; the values are kept.
   */
  canResume?: (statePath: string) => boolean;
  /** overrides the exact-instance match; used when adopting a legacy record */
  acceptInstance?: (storedInstanceId?: number | string) => boolean;
}

export const loadFormDraft = (
  draftKey: string,
  options: FormDraftLoadOptions
): Promise<FormDraftPayload | undefined> => {
  // once the caller has timed out and moved on, the still-running load must
  // not delete the key on an invalid verdict — its snapshot is stale and a
  // fresh draft may have been written to the same key since
  const raceState = { timedOut: false };
  return withTimeout(
    loadFormDraftUnbounded(draftKey, options, raceState),
    undefined,
    FORM_DRAFT_LOAD_TIMEOUT_MS,
    () => {
      raceState.timedOut = true;
    }
  );
};

type RawRead =
  | { status: 'ok'; payload: FormDraftPayload }
  | { status: 'absent' }
  | { status: 'failed' };

const readFormDraftRaw = async (draftKey: string): Promise<RawRead> => {
  let ciphertext: string | null;
  try {
    ciphertext = await AsyncStorage.getItem(draftKey);
  } catch {
    return { status: 'failed' };
  }
  if (!ciphertext) return { status: 'absent' };
  try {
    const cipherKey = await getCipherKey();
    const plaintext = CryptoJS.AES.decrypt(ciphertext, cipherKey).toString(
      CryptoJS.enc.Utf8
    );
    return { status: 'ok', payload: JSON.parse(plaintext) as FormDraftPayload };
  } catch {
    return { status: 'absent' };
  }
};

export interface RestoreFormDraftParams extends Omit<
  FormDraftLoadOptions,
  'acceptInstance'
> {
  userId: number | string;
  formTemplateId: number | string;
}

/**
 * Restore the draft for one form instance. Records written by older builds
 * (or before the first save assigned an instance) live under the
 * instance-less key; they are adopted once and moved to the instance key.
 */
export const adoptLegacyFormDraft = async ({
  userId,
  formTemplateId,
  instanceId,
  ...options
}: RestoreFormDraftParams): Promise<FormDraftPayload | undefined> => {
  if (instanceId == null) return undefined;
  const key = formDraftKey({ userId, formTemplateId, instanceId });
  const legacyKey = formDraftKey({ userId, formTemplateId });
  return chainWrite(key, async () => {
    await writeChains.get(legacyKey);
    if ((await readFormDraftRaw(key)).status !== 'absent') return undefined;
    const legacy = await loadFormDraft(legacyKey, {
      ...options,
      instanceId,
      acceptInstance: stored =>
        stored == null || sameInstance(stored, instanceId)
    });
    if (!legacy) return undefined;
    const { savedAt, ...fields } = legacy;
    await saveFormDraft(key, { ...fields, instanceId }, savedAt);
    await clearFormDraft(legacyKey);
    return { ...fields, instanceId, savedAt };
  });
};

export const restoreFormDraft = async (
  params: RestoreFormDraftParams
): Promise<FormDraftPayload | undefined> => {
  const { userId, formTemplateId, instanceId, ...options } = params;
  if (instanceId == null) {
    return loadFormDraft(formDraftKey({ userId, formTemplateId }), {
      ...options,
      instanceId
    });
  }
  const own = await loadFormDraft(
    formDraftKey({ userId, formTemplateId, instanceId }),
    { ...options, instanceId }
  );
  return own ?? adoptLegacyFormDraft(params);
};

const loadFormDraftUnbounded = async (
  draftKey: string,
  {
    formTemplateVersion,
    instanceId,
    now = Date.now(),
    canResume,
    acceptInstance = stored => sameInstance(stored, instanceId)
  }: FormDraftLoadOptions,
  raceState: { timedOut: boolean }
): Promise<FormDraftPayload | undefined> => {
  // a logout wipe during the load invalidates whatever it read
  const epoch = draftEpoch;
  let ciphertext: string | null = null;
  try {
    ciphertext = await AsyncStorage.getItem(draftKey);
  } catch {
    return undefined;
  }
  if (!ciphertext) return undefined;
  let cipherKey: string;
  try {
    cipherKey = await getCipherKey();
  } catch {
    return undefined;
  }
  try {
    const plaintext = CryptoJS.AES.decrypt(ciphertext, cipherKey).toString(
      CryptoJS.enc.Utf8
    );
    const payload = JSON.parse(plaintext) as FormDraftPayload;
    const expired = now - payload.savedAt > FORM_DRAFT_TTL_MS;
    const versionMismatch =
      formTemplateVersion !== undefined &&
      payload.formTemplateVersion !== undefined &&
      payload.formTemplateVersion !== formTemplateVersion;
    if (expired || versionMismatch) {
      if (!raceState.timedOut) await clearFormDraft(draftKey);
      return undefined;
    }
    // A draft for a different instance of this template must not leak into
    // the one being opened; keep it (don't delete) so its own instance can
    // usually still recover — overwrites, the completion clear, and TTL bound
    // its lifetime. Recovery is best-effort, not guaranteed: a kill in the
    // brief window right after entity creation can persist a write whose
    // instanceId hadn't caught up yet, which then mismatches here and only
    // costs the resume point (the server keeps every committed section).
    if (!acceptInstance(payload.instanceId)) {
      return undefined;
    }
    if (draftKeyWiped(draftKey, epoch)) return undefined;
    if (payload.statePath && canResume && !canResume(payload.statePath)) {
      const cleaned: FormDraftInput = {
        formValues: payload.formValues,
        history: payload.history,
        formTemplateVersion: payload.formTemplateVersion,
        instanceId: payload.instanceId
      };
      // keep the original timestamp: scrubbing a path must not extend the
      // TTL of answers at rest
      if (!raceState.timedOut) {
        await saveFormDraft(draftKey, cleaned, payload.savedAt);
        if (draftKeyWiped(draftKey, epoch)) {
          await clearFormDraft(draftKey);
          return undefined;
        }
      }
      return { ...cleaned, savedAt: payload.savedAt };
    }
    return payload;
  } catch {
    // undecipherable / corrupt (e.g. cipher key rotated) — discard
    if (!raceState.timedOut) await clearFormDraft(draftKey);
    return undefined;
  }
};

export const clearFormDraft = async (draftKey: string): Promise<void> => {
  try {
    await AsyncStorage.removeItem(draftKey);
  } catch {
    // best-effort; TTL expires anything left behind
  }
};

/**
 * Every wipe bumps the epoch and records which keys it covered, so I/O that
 * was in flight across a wipe can tell whether its own key was removed: a
 * form screen can unmount (and flush) after a logout, and that pending draft
 * must not resurrect the previous account's answers, while the login sweep
 * must not cost the current user their draft.
 */
let draftEpoch = 0;
const WIPE_HISTORY_LIMIT = 16;
const wipes: { epoch: number; matches: (key: string) => boolean }[] = [];

const draftKeyWiped = (key: string, since: number): boolean =>
  draftEpoch - since > WIPE_HISTORY_LIMIT ||
  wipes.some(wipe => wipe.epoch > since && wipe.matches(key));

const removeDraftKeys = async (
  shouldRemove: (key: string) => boolean
): Promise<void> => {
  draftEpoch++;
  wipes.push({ epoch: draftEpoch, matches: shouldRemove });
  if (wipes.length > WIPE_HISTORY_LIMIT) wipes.shift();
  try {
    const keys = await AsyncStorage.getAllKeys();
    const draftKeys = keys.filter(
      key => key.startsWith(DRAFT_KEY_PREFIX) && shouldRemove(key)
    );
    if (draftKeys.length > 0) {
      await AsyncStorage.multiRemove(draftKeys);
    }
  } catch {
    // best-effort; TTL expires anything left behind
  }
};

/** Remove every stored draft — called on an explicit logout. */
export const clearAllFormDrafts = (): Promise<void> =>
  removeDraftKeys(() => true);

/** Remove drafts left by any other account — called once the user is known. */
export const clearOtherUsersFormDrafts = (
  userId: number | string
): Promise<void> => {
  const own = `${DRAFT_KEY_PREFIX}${userId}:`;
  return removeDraftKeys(key => !key.startsWith(own));
};

export type LogoutReason =
  'user' | 'session-expired' | 'biometric-cancel' | 'unauthorized';

const DRAFT_KEEPING_LOGOUT_REASONS: ReadonlySet<LogoutReason> = new Set([
  'session-expired',
  'biometric-cancel',
  'unauthorized'
]);

/**
 * Drafts survive a logout the same account is expected to return from
 * (CD-372); an explicit logout still wipes them so nothing is left for the
 * next account on the device.
 */
export const logoutKeepsDrafts = (reason: LogoutReason = 'user'): boolean =>
  DRAFT_KEEPING_LOGOUT_REASONS.has(reason);

/**
 * Restore precedence: the server copy always wins — the draft only fills
 * fields the server copy lacks (null/undefined). A draft must never overwrite
 * newer server data.
 *
 * Assumption: null/undefined on the server means "never answered". If a form
 * ever gains an affordance to explicitly clear an answered field server-side,
 * that cleared value would be indistinguishable and could be backfilled here.
 */
export const mergeDraftValues = <FormValuesType>(
  serverValues: Partial<FormValuesType>,
  draftValues?: Partial<FormValuesType>
): Partial<FormValuesType> => {
  if (!draftValues) return serverValues;
  const merged: Partial<FormValuesType> = { ...draftValues, ...serverValues };
  for (const key of Object.keys(merged) as (keyof FormValuesType)[]) {
    if (
      (merged[key] === null || merged[key] === undefined) &&
      draftValues[key] !== undefined
    ) {
      merged[key] = draftValues[key];
    }
  }
  return merged;
};

export interface DebouncedDraftWriter {
  /** Merge a partial draft update; persisted after the debounce interval. */
  write: (draft: Partial<FormDraftInput>) => void;
  /** Persist any pending update immediately (e.g. on a section transition). */
  flush: () => Promise<void>;
  /** Drop the accumulated draft without persisting (e.g. after completion). */
  cancel: () => void;
}

const definedFields = (
  draft: Partial<FormDraftInput>
): Partial<FormDraftInput> =>
  Object.fromEntries(
    Object.entries(draft).filter(([, value]) => value !== undefined)
  );

const writeChains = new Map<string, Promise<void>>();

const chainWrite = <T>(
  draftKey: string,
  work: () => Promise<T>
): Promise<T> => {
  const next = (writeChains.get(draftKey) ?? Promise.resolve()).then(work);
  const settled = next
    .catch(() => undefined)
    .then(() => {
      if (writeChains.get(draftKey) === settled) writeChains.delete(draftKey);
    });
  writeChains.set(draftKey, settled);
  return next;
};

export const createDebouncedDraftWriter = (
  draftKey: string,
  delayMs: number,
  options: { canResume?: (statePath: string) => boolean } = {}
): DebouncedDraftWriter => {
  // updates accumulate across persists so a values-only write after a section
  // transition still carries the last statePath (and vice versa)
  let accumulated: Partial<FormDraftInput> = {};
  let dirty = false;
  let pendingEpoch = draftEpoch;
  let timer: ReturnType<typeof setTimeout> | undefined;

  const persist = async () => {
    if (timer !== undefined) {
      clearTimeout(timer);
      timer = undefined;
    }
    if (!dirty) return;
    dirty = false;
    // queued before a logout wipe — drop rather than resurrect the previous
    // account's answers
    if (draftKeyWiped(draftKey, pendingEpoch)) {
      accumulated = {};
      return;
    }
    const pending = accumulated;
    accumulated = {};
    await chainWrite(draftKey, async () => {
      const read = await readFormDraftRaw(draftKey);
      if (read.status === 'failed') {
        accumulated = { ...pending, ...accumulated };
        dirty = true;
        return;
      }
      const sameVersion =
        read.status === 'ok' &&
        (pending.formTemplateVersion === undefined ||
          read.payload.formTemplateVersion === undefined ||
          read.payload.formTemplateVersion === pending.formTemplateVersion);
      const stored =
        sameVersion && read.status === 'ok' ? read.payload : undefined;
      await saveFormDraft(draftKey, {
        ...stored,
        ...pending,
        formValues: { ...stored?.formValues, ...pending.formValues }
      });
    });
    // the wipe bumps the epoch before it removes keys, so a write that was
    // already past the check above can land after the removal — undo it
    if (draftKeyWiped(draftKey, pendingEpoch)) await clearFormDraft(draftKey);
  };

  return {
    write: draft => {
      if (
        draft.statePath &&
        options.canResume &&
        !options.canResume(draft.statePath)
      ) {
        const rest = { ...draft };
        delete rest.statePath;
        draft = rest;
        if (draft.formValues === undefined) {
          accumulated = { ...accumulated, ...definedFields(draft) };
          return;
        }
      }
      accumulated = { ...accumulated, ...definedFields(draft) };
      dirty = true;
      pendingEpoch = draftEpoch;
      if (timer !== undefined) clearTimeout(timer);
      timer = setTimeout(persist, delayMs);
    },
    flush: persist,
    cancel: () => {
      if (timer !== undefined) {
        clearTimeout(timer);
        timer = undefined;
      }
      accumulated = {};
      dirty = false;
    }
  };
};
